import { useEffect, useState } from 'react'
import RoutesMap from 'route_control/RouteControl'
import { Form } from 'react-bootstrap'
import { useFormik } from 'formik'
import { Text, TextArea, SelectBox, Email, Password, File, Number, SelectStatusBox, DatePicker } from 'components/input_fields'
import { AddUserSchema } from './ValidationSchema'
import { SaveButton } from 'components/buttons'
import { Loader } from 'components/loader'
import { UserRound, MapPin, CircleUserRound } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { userRoleList, userTypeList } from '../../../../redux/slices/settings'
import { countryList, stateByCountry, cityByState } from '../../../../redux/slices/master'
import { addUser } from '../../../../redux/slices/users'
import { addUpdateHandler } from 'utils'
import { useTranslation } from 'react-i18next'

export default function AddUserForm() {

    const [loading, setLoading] = useState(true)
    const [spinner, setSpinner] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const role = useSelector(state => state?.userRole?.list)
    const userType = useSelector(state => state?.userType?.list)
    const country = useSelector(state => state?.country?.list)
    const state = useSelector(state => state?.state?.byCountry)
    const city = useSelector(state => state?.city?.byState)
    const stateLoading = useSelector(state => state?.state?.loading)
    const cityLoading = useSelector(state => state?.city?.loading)

    const role_option = role?.map(val => (
        { "value": val.id, "label": val.role_name }
    ))

    const user_type_option = userType?.map(val => (
        { "value": val.id, "label": val.user_type_name }
    ))

    const country_option = country?.map(val => (
        { "value": val.id, "label": val.country }
    ))

    const state_option = state?.map(val => (
        { "value": val.id, "label": val.state }
    ))

    const city_option = city?.map(val => (
        { "value": val.id, "label": val.city }
    ))

    const gender_option = [
        { "value": 'Male', "label": 'Male' },
        { "value": 'Female', "label": 'Female' },
        { "value": 'Other', "label": 'Other' },
    ]

    const initialValues = {
        role_id: '',
        type_id: '',
        fname: '',
        lname: '',
        mobile: '',
        email: '',
        password: '',
        confirmPassword: '',
        country: '',
        state: '',
        city: '',
        pincode: '',
        address: '',
        gender: '',
        dob: '',
        profile: '',
        status: 'Active'
    }

    const { values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, setFieldTouched } = useFormik({
        initialValues: initialValues,
        validationSchema: AddUserSchema,
        enableReinitialize: true,

        onSubmit: (values, action) => {
            setSpinner(true)
            dispatch(addUser({ 'values': values })).then((res) => addUpdateHandler({
                res: res.payload,
                action,
                navigate,
                success_toast: `${t('user.user')} ${t('common.success_add')}`,
                wrong_toast: `${t('common.wrong')}`,
                navigate_route: RoutesMap.USER_LIST.path,
                spinner: setSpinner,
            }))
        },
    })

    const load_state = (id) => {
        if (id) {
            dispatch(stateByCountry({ 'id': id, navigate }))
            setFieldValue('state', '')
            setFieldValue('city', '')
        } else {
            setFieldValue('state', '')
            setFieldValue('city', '')
        }
    }

    const load_city = (id) => {
        if (id) {
            dispatch(cityByState({ 'id': id, navigate }))
            setFieldValue('city', '')
        } else {
            setFieldValue('city', '')
        }
    }

    useEffect(() => {
        setLoading(true)
        Promise.all([
            dispatch(userRoleList({ navigate })),
            dispatch(userTypeList({ navigate })),
            dispatch(countryList({ navigate })),
        ]).finally(() => setLoading(false));
    }, [])

    return (
        <>
            <div className="container-fluid">
                <div className='layout'>
                    {loading ?
                        <div className="row">
                            <div className="col-md-12">
                                <Loader />
                            </div>
                        </div>
                        :
                        <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                            <div className="row">

                                <div className="col-md-12 mb-2">
                                    <h6 className="form_section_title">
                                        <UserRound size={18} />
                                        {t('user.basic_info')}
                                    </h6>
                                </div>

                                <div className="col-md-3">
                                    <SelectBox
                                        label_name={t('user.role')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        option={role_option}
                                        name='role_id'
                                        value={values.role_id}
                                        onChange={(e) =>
                                            setFieldValue('role_id', e)
                                        }
                                        onBlur={() => setFieldTouched("role_id", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.role_id && touched.role_id ? (<span className='field_error'>{errors.role_id}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <SelectBox
                                        label_name={t('user.user_type')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        option={user_type_option}
                                        name='type_id'
                                        value={values.type_id}
                                        onChange={(e) =>
                                            setFieldValue('type_id', e)
                                        }
                                        onBlur={() => setFieldTouched("type_id", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.type_id && touched.type_id ? (<span className='field_error'>{errors.type_id}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Text
                                        label_name={t('user.fname')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='fname'
                                        value={values.fname}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.fname && touched.fname ? (<span className='field_error'>{errors.fname}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Text
                                        label_name={t('user.lname')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='lname'
                                        value={values.lname}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.lname && touched.lname ? (<span className='field_error'>{errors.lname}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Number
                                        label_name={t('user.mobile')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='mobile'
                                        value={values.mobile}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.mobile && touched.mobile ? (<span className='field_error'>{errors.mobile}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Email
                                        label_name={t('user.email')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='email'
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.email && touched.email ? (<span className='field_error'>{errors.email}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Password
                                        label_name={t('user.password')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='password'
                                        value={values.password}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        showPassword={showPassword}
                                        setShowPassword={setShowPassword}
                                        error={errors.password && touched.password ? (<span className='field_error'>{errors.password}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Password
                                        label_name={t('user.confirm_password')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='confirmPassword'
                                        value={values.confirmPassword}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        showPassword={showConfirmPassword}
                                        setShowPassword={setShowConfirmPassword}
                                        error={errors.confirmPassword && touched.confirmPassword ? (<span className='field_error'>{errors.confirmPassword}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-12 mt-3 mb-2">
                                    <h6 className="form_section_title">
                                        <MapPin size={18} />
                                        {t('user.address_info')}
                                    </h6>
                                </div>

                                <div className="col-md-3">
                                    <SelectBox
                                        label_name={t('user.country')}
                                        mandatory={false}
                                        placeholder=''
                                        disabled={false}
                                        option={country_option}
                                        name='country'
                                        value={values.country}
                                        onChange={(e) => {
                                            load_state(e);
                                            setFieldValue('country', e);
                                        }}
                                        onBlur={() => setFieldTouched("country", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.country && touched.country ? (<span className='field_error'>{errors.country}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <SelectBox
                                        label_name={t('user.state')}
                                        mandatory={false}
                                        placeholder=''
                                        disabled={!values.country}
                                        loading={stateLoading}
                                        option={state_option}
                                        name='state'
                                        value={values.state}
                                        onChange={(e) => {
                                            load_city(e);
                                            setFieldValue('state', e);
                                        }}
                                        onBlur={() => setFieldTouched("state", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.state && touched.state ? (<span className='field_error'>{errors.state}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <SelectBox
                                        label_name={t('user.city')}
                                        mandatory={false}
                                        placeholder=''
                                        disabled={!values.state}
                                        loading={cityLoading}
                                        option={city_option}
                                        name='city'
                                        value={values.city}
                                        onChange={(e) =>
                                            setFieldValue('city', e)
                                        }
                                        onBlur={() => setFieldTouched("city", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.city && touched.city ? (<span className='field_error'>{errors.city}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Number
                                        label_name={t('user.pincode')}
                                        mandatory={false}
                                        placeholder=''
                                        disabled={false}
                                        name='pincode'
                                        value={values.pincode}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.pincode && touched.pincode ? (<span className='field_error'>{errors.pincode}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <TextArea
                                        label_name={t('user.address')}
                                        mandatory={false}
                                        placeholder=''
                                        disabled={false}
                                        rows={2}
                                        name='address'
                                        value={values.address}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.address && touched.address ? (<span className='field_error'>{errors.address}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-12 mt-3 mb-2">
                                    <h6 className="form_section_title">
                                        <CircleUserRound size={18} />
                                        {t('user.other_info')}
                                    </h6>
                                </div>

                                <div className="col-md-3">
                                    <SelectBox
                                        label_name={t('user.gender')}
                                        mandatory={false}
                                        placeholder=''
                                        disabled={false}
                                        option={gender_option}
                                        name='gender'
                                        value={values.gender}
                                        onChange={(e) =>
                                            setFieldValue('gender', e)
                                        }
                                        onBlur={() => setFieldTouched("gender", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.gender && touched.gender ? (<span className='field_error'>{errors.gender}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <DatePicker
                                        label_name={t('user.dob')}
                                        mandatory={false}
                                        placeholder=''
                                        name='dob'
                                        value={values.dob}
                                        onChange={(date, dateString) => setFieldValue('dob', dateString)}
                                        onBlur={() => setFieldTouched('dob', true)}
                                        error={errors.dob && touched.dob ? (<span className='field_error'>{errors.dob}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <File
                                        label_name={t('user.profile')}
                                        mandatory={false}
                                        disabled={false}
                                        name='profile'
                                        onChange={(e) =>
                                            setFieldValue('profile', e.currentTarget.files[0])
                                        }
                                        onBlur={handleBlur}
                                        error={errors.profile && touched.profile ? (<span className='field_error'>{errors.profile}</span>) : null}
                                        accept="image/jpg, image/jpeg, image/png"
                                    />
                                </div>

                                <div className="col-md-3">
                                    <SelectStatusBox
                                        label_name={t('user.status')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={true}
                                        name='status'
                                        value={values.status}
                                        onChange={(e) =>
                                            setFieldValue('status', e)
                                        }
                                        onBlur={() => setFieldTouched("status", true)}
                                        error={errors.status && touched.status ? (<span className='field_error'>{errors.status}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-12">
                                    <div className="d-flex justify-content-end">
                                        <div className="col-lg-2">
                                            <SaveButton
                                                button_name={t('common.submit')}
                                                spinner={spinner}
                                            />
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </Form>
                    }
                </div>
            </div>
        </>
    )
}